import {
  stripAlertTitlePrefix,
  normalizeTimeoutDownMessage,
  normalizeInfluxAnomalyMessage,
  buildTargetStatusPopupDescription,
} from './alertDisplay';
import { normalizeInfluxStatus, isInfluxUpStatus } from './influxStatus';

const MAX_ALERTS = 50;
const ACK_KEYS_STORAGE = 'pingpulse_acknowledged_keys';
const LAST_INFLUX_ALERT_ID_KEY = 'pingpulse_influx_last_alert_id';

function loadLastInfluxAlertIds() {
  try {
    const raw = localStorage.getItem(LAST_INFLUX_ALERT_ID_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    return obj && typeof obj === 'object' ? obj : {};
  } catch {
    return {};
  }
}

export function loadAcknowledgedKeys() {
  try {
    const raw = localStorage.getItem(ACK_KEYS_STORAGE);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function persistAcknowledgedKeys(keys) {
  localStorage.setItem(ACK_KEYS_STORAGE, JSON.stringify([...keys]));
}

/** Remember dismissed incident until target status changes (prevents re-sync spam). */
export function acknowledgeAlertKey(key) {
  if (!key) return;
  const keys = loadAcknowledgedKeys();
  keys.add(key);
  persistAcknowledgedKeys(keys);
}

export function acknowledgeAllAlertKeys(alerts) {
  const keys = loadAcknowledgedKeys();
  (alerts || []).forEach((alert) => {
    const key = getAlertDedupeKey(alert);
    if (key) keys.add(key);
  });
  persistAcknowledgedKeys(keys);
}

/** New status on target/influx — allow fresh alerts for that source. */
export function clearAcknowledgedKeysForTarget(targetId) {
  if (targetId == null) return;
  const id = String(targetId);
  const keys = loadAcknowledgedKeys();
  let changed = false;
  for (const key of keys) {
    if (
      key.startsWith(`target-status:${id}:`)
      || key.startsWith(`influxdb:${id}:`)
    ) {
      keys.delete(key);
      changed = true;
    }
  }
  if (changed) persistAcknowledgedKeys(keys);
}

/** Clear Influx acknowledge keys when monitor status changes (e.g. ANOMALY → OK). */
export function clearInfluxAckOnStatusChange(data) {
  const targetId = data?.influxTargetId ?? data?.targetId;
  if (targetId == null) return;
  const status = normalizeInfluxStatus(data.status) || String(data.status || '').toUpperCase();
  const prev = data.previousStatus != null
    ? (normalizeInfluxStatus(data.previousStatus) || String(data.previousStatus).toUpperCase())
    : null;

  if (prev != null && prev !== status) {
    clearAcknowledgedKeysForTarget(targetId);
    return;
  }
  if (isInfluxUpStatus(status)) {
    clearAcknowledgedKeysForTarget(targetId);
    return;
  }
  // Re-entering failure from healthy/unknown — stale ack from prior incident
  if (
    (status === 'ANOMALY' || status === 'DOWN' || status === 'ERROR')
    && (prev == null || isInfluxUpStatus(prev) || prev === 'UNKNOWN')
  ) {
    clearAcknowledgedKeysForTarget(targetId);
  }
}

/** Run before showing Influx popup — clears stale ack after OK/recovery or status change. */
export function prepareInfluxAlertAck(data) {
  const targetId = data?.influxTargetId ?? data?.targetId;

  clearInfluxAckOnStatusChange({
    influxTargetId: data?.influxTargetId ?? data?.targetId,
    targetId: data?.targetId ?? data?.influxTargetId,
    status: data?.status,
    previousStatus: data?.previousStatus,
  });

  // New persisted alert row after recovery (or re-open) — not the same incident as ack
  const alertId = data?.alertId;
  if (targetId == null || alertId == null) return;

  const map = loadLastInfluxAlertIds();
  const tid = String(targetId);
  const prevAlertId = map[tid];
  const nextAlertId = String(alertId);
  if (prevAlertId != null && prevAlertId !== nextAlertId) {
    clearAcknowledgedKeysForTarget(targetId);
  }
  map[tid] = nextAlertId;
  localStorage.setItem(LAST_INFLUX_ALERT_ID_KEY, JSON.stringify(map));
}

export function isAlertAcknowledged(alert) {
  const key = getAlertDedupeKey(alert);
  return key ? loadAcknowledgedKeys().has(key) : false;
}

/** Socket payload — same dedupe key as Active Alerts panel. */
export function isTargetStatusAcknowledged(data) {
  if (!data?.targetId) return false;
  return isAlertAcknowledged({
    type: 'target-status',
    status: data.status,
    data: {
      targetId: data.targetId,
      status: data.status,
      isRecovery: data.isRecovery,
    },
  });
}

export function isInfluxAlertAcknowledged(data) {
  const targetId = data?.targetId ?? data?.influxTargetId;
  if (targetId == null) return false;
  return isAlertAcknowledged({
    type: 'influxdb',
    status: data.status,
    data: {
      targetId,
      status: data.status,
    },
  });
}

export function clearHardwareAckForAgent(agentId) {
  if (agentId == null) return;
  const key = `hardware:${String(agentId)}`;
  const keys = loadAcknowledgedKeys();
  if (!keys.has(key)) return;
  keys.delete(key);
  persistAcknowledgedKeys(keys);
}

/** Hardware recovered (no issues) — allow fresh alerts on the next incident. */
export function clearHardwareAckWhenHealthy(data) {
  const agentId = data?.agent_id ?? data?.agentId;
  if (agentId == null) return;
  const issues = data?.summary?.issues;
  const overall = data?.summary?.overall;
  const healthy = (
    (Array.isArray(issues) && issues.length === 0)
    || overall === 'Healthy'
    || overall === 'OK'
  );
  if (healthy) clearHardwareAckForAgent(agentId);
}

export function isHardwareAlertAcknowledged(data) {
  const agentId = data?.agent_id ?? data?.agentId;
  if (agentId == null) return false;
  return isAlertAcknowledged({
    type: 'hardware',
    data: { agent_id: agentId },
  });
}

function pickTargetId(alert) {
  const d = alert.data || {};
  return (
    alert.targetId
    ?? d.targetId
    ?? d.target_id
    ?? d.influxTargetId
    ?? d.influx_target_id
    ?? d.target?.id
    ?? null
  );
}

function pickAgentId(alert) {
  const d = alert.data || {};
  return (
    d.agent_id
    ?? d.agentId
    ?? alert.agentId
    ?? alert.targetId
    ?? d.targetId
    ?? null
  );
}

/** Stable key so the same incident does not stack on refresh or socket replay. */
export function getAlertDedupeKey(alert) {
  if (!alert) return '';
  const type = alert.type || 'unknown';
  const d = alert.data || {};

  switch (type) {
    case 'target-status': {
      const targetId = pickTargetId(alert);
      const status = d.status ?? alert.status ?? 'UNKNOWN';
      if (d.isRecovery) {
        return `target-status:${targetId}:recovery`;
      }
      return `target-status:${targetId}:${status}`;
    }
    case 'monitored-item': {
      const agentId = pickAgentId(alert);
      const name = d.item_name ?? d.itemName ?? '';
      if (d.is_recovery) {
        return `monitored-item:${agentId}:${name}:recovery`;
      }
      return `monitored-item:${agentId}:${name}`;
    }
    case 'hardware':
      return `hardware:${pickAgentId(alert)}`;
    case 'event-anomaly':
      return `event-anomaly:${pickAgentId(alert)}`;
    case 'influxdb': {
      const targetId = pickTargetId(alert) ?? d.targetId ?? d.target_id;
      const status = d.status ?? alert.status ?? 'UNKNOWN';
      return `influxdb:${targetId}:${status}`;
    }
    case 'resource':
      return `resource:${pickAgentId(alert)}`;
    case 'test':
      return alert.id;
    default:
      return alert.id || `${type}:${alert.timestamp}`;
  }
}

function alertTimestamp(alert) {
  const t = alert.timestamp || alert.createdAt;
  const ms = t ? new Date(t).getTime() : 0;
  return Number.isFinite(ms) ? ms : 0;
}

function pickPreferred(existing, incoming) {
  if (!existing) return incoming;
  if (!incoming) return existing;
  if (incoming.isServerAlert && !existing.isServerAlert) return incoming;
  if (existing.isServerAlert && !incoming.isServerAlert) return existing;
  return alertTimestamp(incoming) >= alertTimestamp(existing) ? incoming : existing;
}

export function mergeAlerts(...lists) {
  const map = new Map();
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const alert of list) {
      if (!alert) continue;
      const key = getAlertDedupeKey(alert);
      map.set(key, pickPreferred(map.get(key), alert));
    }
  }
  return Array.from(map.values())
    .sort((a, b) => alertTimestamp(b) - alertTimestamp(a))
    .slice(0, MAX_ALERTS);
}

export function upsertAlert(alerts, newAlert) {
  const key = getAlertDedupeKey(newAlert);
  const map = new Map();
  for (const alert of alerts) {
    if (!alert) continue;
    map.set(getAlertDedupeKey(alert), alert);
  }
  const existing = map.get(key);
  const merged = {
    ...existing,
    ...newAlert,
    id: existing?.id ?? newAlert.id,
    serverId: newAlert.serverId ?? existing?.serverId ?? null,
    isServerAlert: Boolean(newAlert.serverId ?? existing?.serverId),
    pulseCount: (existing?.pulseCount || 0) + 1,
    timestamp: newAlert.timestamp || existing?.timestamp,
  };
  map.set(key, merged);
  return Array.from(map.values())
    .sort((a, b) => alertTimestamp(b) - alertTimestamp(a))
    .slice(0, MAX_ALERTS);
}

export function removeActiveAlertsForTargetId(alerts, targetId, types = ['target-status', 'influxdb']) {
  if (targetId == null) return alerts || [];
  const id = String(targetId);
  return (alerts || []).filter((alert) => {
    if (!types.includes(alert.type)) return true;
    const d = alert.data || {};
    const alertTargetId = String(
      alert.targetId
      ?? d.targetId
      ?? d.target_id
      ?? d.influxTargetId
      ?? ''
    );
    return alertTargetId !== id;
  });
}

function targetIdsMatch(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

export function patchTargetInList(targets, data) {
  if (!Array.isArray(targets) || !data?.targetId) return targets || [];
  return targets.map((target) => (
    targetIdsMatch(target.id, data.targetId)
      ? {
        ...target,
        last_status: data.status,
        last_latency: data.latency ?? target.last_latency,
        last_check: data.timestamp ?? target.last_check,
        last_error: data.errorMessage ?? target.last_error,
        last_message: data.message ?? target.last_message,
        consecutive_failures: data.consecutiveFailures ?? target.consecutive_failures,
        consecutive_timeout_threshold: data.consecutiveThreshold ?? target.consecutive_timeout_threshold,
      }
      : target
  ));
}

export function removeActiveAlertsForHardwareAgent(alerts, agentId) {
  if (agentId == null) return alerts || [];
  const id = String(agentId);
  return (alerts || []).filter((alert) => {
    if (alert.type !== 'hardware') return true;
    const d = alert.data || {};
    const aid = String(d.agent_id ?? d.agentId ?? alert.agentId ?? '');
    return aid !== id;
  });
}

/** Match backend resourceAlertService thresholds. */
export const RESOURCE_ALERT_THRESHOLDS = {
  cpu: 70,
  memory: 70,
  disk: 80,
};

function pickAgentIdFromData(data) {
  return data?.agent_id ?? data?.agentId ?? null;
}

export function isResourceMetricsHealthy(data) {
  const cpu = data?.cpu?.usage_percent ?? 0;
  const mem = data?.memory?.usage_percent ?? 0;
  const disk = data?.disk?.usage_percent ?? 0;
  return (
    cpu <= RESOURCE_ALERT_THRESHOLDS.cpu
    && mem <= RESOURCE_ALERT_THRESHOLDS.memory
    && disk <= RESOURCE_ALERT_THRESHOLDS.disk
  );
}

export function buildResourceBreachDescription(data) {
  const cpu = data?.cpu?.usage_percent ?? 0;
  const mem = data?.memory?.usage_percent ?? 0;
  const disk = data?.disk?.usage_percent ?? 0;
  const lines = [];
  if (cpu > RESOURCE_ALERT_THRESHOLDS.cpu) {
    lines.push(`CPU: ${cpu.toFixed(1)}% (threshold: ${RESOURCE_ALERT_THRESHOLDS.cpu}%)`);
  }
  if (mem > RESOURCE_ALERT_THRESHOLDS.memory) {
    lines.push(`MEMORY: ${mem.toFixed(1)}% (threshold: ${RESOURCE_ALERT_THRESHOLDS.memory}%)`);
  }
  if (disk > RESOURCE_ALERT_THRESHOLDS.disk) {
    lines.push(`DISK: ${disk.toFixed(1)}% (threshold: ${RESOURCE_ALERT_THRESHOLDS.disk}%)`);
  }
  return lines.join(', ');
}

export function removeActiveAlertsForAgent(alerts, agentId, types) {
  if (agentId == null || !Array.isArray(types) || types.length === 0) return alerts || [];
  const id = String(agentId);
  const typeSet = new Set(types);
  return (alerts || []).filter((alert) => {
    if (!typeSet.has(alert.type)) return true;
    const d = alert.data || {};
    const aid = String(d.agent_id ?? d.agentId ?? alert.agentId ?? '');
    return aid !== id;
  });
}

export function removeMonitoredItemAlerts(alerts, agentId, itemName) {
  if (agentId == null) return alerts || [];
  const aid = String(agentId);
  const name = itemName != null ? String(itemName) : null;
  return (alerts || []).filter((alert) => {
    if (alert.type !== 'monitored-item') return true;
    const d = alert.data || {};
    const alertAgentId = String(d.agent_id ?? d.agentId ?? alert.agentId ?? '');
    if (alertAgentId !== aid) return true;
    if (name == null) return false;
    const alertName = String(d.item_name ?? d.itemName ?? '');
    return alertName !== name;
  });
}

export function isEventAnomalyClear(data) {
  return (data?.total_errors ?? 0) === 0 && (data?.total_warnings ?? 0) === 0;
}

/** Drop or refresh resource alerts when live agent metrics change. */
export function applyResourceMetricsToAlerts(alerts, data) {
  const agentId = pickAgentIdFromData(data);
  if (agentId == null) return alerts || [];
  if (isResourceMetricsHealthy(data)) {
    return removeActiveAlertsForAgent(alerts, agentId, ['resource']);
  }
  const description = buildResourceBreachDescription(data);
  if (!description) return alerts || [];
  const id = String(agentId);
  let found = false;
  const next = (alerts || []).map((alert) => {
    if (alert.type !== 'resource') return alert;
    const d = alert.data || {};
    const aid = String(d.agent_id ?? d.agentId ?? alert.agentId ?? '');
    if (aid !== id) return alert;
    found = true;
    return {
      ...alert,
      description,
      data: { ...d, ...data },
    };
  });
  return found ? next : alerts || [];
}

/** Drop monitor alerts whose target is already UP/OK (e.g. after refresh). */
export function pruneRecoveredMonitorAlerts(alerts, targets) {
  if (!Array.isArray(alerts)) return [];
  const upIds = new Set(
    (targets || [])
      .filter((t) => {
        const s = String(t.last_status ?? t.status ?? '').toUpperCase();
        return s === 'UP' || s === 'OK';
      })
      .map((t) => String(t.id))
  );
  if (upIds.size === 0) return alerts;
  return alerts.filter((alert) => {
    if (alert.type !== 'target-status') return true;
    const tid = String(pickTargetId(alert) ?? '');
    return !upIds.has(tid);
  });
}

export function pruneRecoveredInfluxAlerts(alerts, influxStatusById) {
  if (!Array.isArray(alerts) || !influxStatusById) return alerts || [];
  return alerts.filter((alert) => {
    if (alert.type !== 'influxdb') return true;
    const tid = String(pickTargetId(alert) ?? '');
    if (!tid) return true;
    const status = influxStatusById[tid];
    if (!status) return true;
    const s = String(status).toUpperCase();
    return s !== 'UP' && s !== 'OK';
  });
}

/** Build `{ [targetId]: status }` from Influx target rows (API or socket). */
export function buildInfluxStatusMap(targets, existing = {}) {
  const map = { ...existing };
  for (const t of targets || []) {
    if (t?.id == null) continue;
    const st = t.last_status ?? t.status;
    if (st != null && st !== '') {
      map[String(t.id)] = st;
    }
  }
  return map;
}

/** Badge = number of distinct active alert rows (not pulse sum). */
export function countAlertBadge(alerts) {
  if (!Array.isArray(alerts)) return 0;
  return alerts.length;
}

/** Ensure DOWN targets appear in Active Alerts after refresh (missed socket). */
export function syncFailureAlertsFromTargets(targets, alerts) {
  if (!Array.isArray(targets)) return alerts || [];
  let next = Array.isArray(alerts) ? alerts : [];
  for (const t of targets) {
    const status = t.last_status || t.lastStatus;
    if (!status || status !== 'DOWN') continue;
    const popupData = {
      targetId: t.id,
      status,
      host: t.host,
      port: t.port,
      errorMessage: t.last_error || t.last_message || t.last_error_message || status,
      message: t.last_message,
      timestamp: t.last_check || t.lastCheck || new Date().toISOString(),
    };
    const draft = {
      id: `target-sync-${t.id}-${status}`,
      type: 'target-status',
      severity: status === 'DOWN' ? 'CRITICAL' : 'HIGH',
      title: `${status}: ${t.name || t.host}`,
      description: buildTargetStatusPopupDescription(popupData, t),
      timestamp: popupData.timestamp,
      data: {
        targetId: t.id,
        targetName: t.name || null,
        status,
        host: t.host,
        port: t.port,
        latency: t.last_latency ?? t.lastLatency,
        errorMessage: popupData.errorMessage,
      },
    };
    const key = getAlertDedupeKey(draft);
    if (!next.some((a) => getAlertDedupeKey(a) === key)) {
      next = upsertAlert(next, draft);
    }
  }
  return next;
}

export function mapServerAlert(row) {
  const metadata = row.metadata && typeof row.metadata === 'object' ? row.metadata : {};
  const status = row.status ?? metadata.status;
  const targetName = metadata.targetName || metadata.target_name || metadata.agentName;
  const agentLogTypes = new Set(['monitored-item', 'resource', 'event-anomaly']);
  const title = agentLogTypes.has(row.type)
    ? stripAlertTitlePrefix(row?.title)
    : targetName && status
      ? `${status}: ${targetName}`
      : stripAlertTitlePrefix(row?.title);
  const description = row.type === 'target-status'
    ? buildTargetStatusPopupDescription(
      {
        status,
        host: row.targetHost ?? metadata.targetHost,
        port: row.targetPort ?? metadata.targetPort,
        errorMessage: row.errorMessage ?? row.description,
        message: row.description,
        timestamp: row.createdAt,
        emailThreshold: metadata.emailThreshold,
        emailConsecutiveFailures: metadata.emailConsecutiveFailures,
        emailThresholdReached: metadata.emailThresholdReached,
        emailSent: metadata.emailSent,
      },
      { host: row.targetHost, port: row.targetPort, name: targetName }
    )
    : row.type === 'influxdb' && (row.status === 'ANOMALY' || metadata.status === 'ANOMALY')
      ? normalizeInfluxAnomalyMessage(row.description, metadata.anomalyDetails)
      : normalizeTimeoutDownMessage(row.description);
  return {
    id: `server-${row.id}`,
    type: row.type,
    severity: row.severity,
    title,
    description,
    timestamp: row.createdAt,
    targetId: row.targetId,
    status,
    data: {
      ...metadata,
      targetId: row.targetId,
      agent_id: row.targetId,
      agentId: row.targetId,
      targetName: targetName || metadata.agentName || null,
      status,
      host: row.targetHost,
      port: row.targetPort,
    },
    autoDismiss: false,
    isServerAlert: true,
    serverId: row.id,
  };
}
